import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setPaymentDetails } from '../CHECK-IN/Walk-In/redux/action';
import ConfirmPayment from './ConfirmPayment';
import { Link, useNavigate } from 'react-router-dom';
import { FaWallet, FaMobileAlt, FaCreditCard } from 'react-icons/fa';
import { IoMdArrowRoundBack } from 'react-icons/io'; // Import the back arrow icon

function Cash() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isContentBlurred, setIsContentBlurred] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('Cash');

  // Fetch booking details and guest info from local storage
  const [bookingDetails, setBookingDetails] = useState(
    JSON.parse(localStorage.getItem('bookingDetails')) || {}
  );

  // Extract pricing details from stored booking details
  const [pricingDetails, setPricingDetails] = useState({
    baseAmount: 0,
    taxAmount: 0,
    finalAmount: 0,
  });

  useEffect(() => {
    const storedBookingDetails = JSON.parse(localStorage.getItem('bookingDetails'));
    if (storedBookingDetails) {
      setBookingDetails(storedBookingDetails);

      // Extract pricing details from local storage
      const storedPricingDetails = {
        baseAmount: parseFloat(localStorage.getItem('baseAmount')) || 0,
        taxAmount: parseFloat(localStorage.getItem('taxAmount')) || 0,
        finalAmount: parseFloat(localStorage.getItem('finalAmount')) || 0,
      };
      setPricingDetails(storedPricingDetails);
    }
  }, []);

  const baseAmount = pricingDetails.baseAmount || 0;
  const taxAmount = pricingDetails.taxAmount || 0;
  const finalAmount = pricingDetails.finalAmount || 0;

  const guestInfo = bookingDetails.guest || {};
  const grandTotal = finalAmount || 0;

  const formatCurrency = (amount) => {
    return `₹${amount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
  };

  const handleVerifyPayClick = () => {
    setIsModalOpen(true);
    setIsContentBlurred(true);
  };

  const handleProceed = () => {
    dispatch(
      setPaymentDetails({
        paymentMethod: selectedPaymentMethod,
        totalAmount: grandTotal.toFixed(2),
      })
    );
    setIsModalOpen(false);
    setIsContentBlurred(false);
    navigate('/key-handover');
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setIsContentBlurred(false); // Ensure the blur effect is removed when modal closes
  };

  return (
    <>
      {/* Back Button Outside of Main Content */}
      <Link to={'/walk-in/form'}>
        <button className="absolute mt-2 top-16 left-3 p-2 text-black hover:text-gray-300 font-semibold text-2xl z-10">
          <IoMdArrowRoundBack />
        </button>
      </Link>

      <div className="w-full h-screen bg-white relative">
        <div className={`w-full h-full ${isContentBlurred ? 'blur-sm' : ''}`}>
          <div className="max-w-5xl mx-auto mt-9 p-6 bg-white rounded-md shadow-sm">
            <div className="bg-gray-200 text-black p-3 mb-6 rounded-md">
              <h2 className="text-xl font-semibold">Choose Your Payment Method</h2>
            </div>

            <div className="flex flex-col md:flex-row gap-6">
              <div className="w-full md:w-1/3">
                <div className="bg-white rounded shadow-md divide-y divide-gray-300">
                  <Link to="/upi-payment">
                    <button
                      className={`py-5 px-4 w-full text-left font-medium flex items-center gap-4 transition-all ${
                        selectedPaymentMethod === 'UPI' ? 'bg-gray-200' : 'hover:bg-gray-200'
                      } border-b border-gray-300`}
                      onClick={() => setSelectedPaymentMethod('UPI')}
                    >
                      <FaMobileAlt />
                      UPI
                    </button>
                  </Link>
                  <Link to="/card-payment">
                    <button
                      className={`py-5 px-4 w-full text-left font-medium flex items-center gap-4 transition-all ${
                        selectedPaymentMethod === 'Credit/Debit/ATM Cards'
                          ? 'bg-gray-200'
                          : 'hover:bg-gray-200'
                      } border-b border-gray-300`}
                      onClick={() => setSelectedPaymentMethod('Credit/Debit/ATM Cards')}
                    >
                      <FaCreditCard />
                      Credit/Debit/ATM Cards
                    </button>
                  </Link>
                  <Link to="/cash">
                    <button
                      className={`py-5 px-4 w-full text-left font-medium flex items-center gap-4 transition-all ${
                        selectedPaymentMethod === 'Cash' ? 'bg-gray-200' : 'hover:bg-gray-200'
                      }`}
                      onClick={() => setSelectedPaymentMethod('Cash')}
                    >
                      <FaWallet />
                      Cash
                    </button>
                  </Link>
                </div>
              </div>

              <div className="w-full md:w-2/3 bg-white p-6 shadow-md rounded-xl">
                <h3 className="text-xl font-semibold mb-6 border-b pb-3">Invoice Summary</h3>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">Guest Name:</span>
                    <span>
                      {guestInfo.firstname} {guestInfo.lastname}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">Phone Number:</span>
                    <span>{guestInfo.phonenumber}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">Email:</span>
                    <span>{guestInfo.emailid}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">Booking Dates:</span>
                    <span>
                      {bookingDetails.booking?.checkindate} to{' '}
                      {bookingDetails.booking?.checkoutdate}
                    </span>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between mb-3 text-sm">
                    <span className="font-medium">BasePrice:</span>
                    <span className="font-medium">{formatCurrency(baseAmount)}</span>
                  </div>
                  <div className="flex justify-between mb-3 text-sm">
                    <span className="font-medium">GST:</span>
                    <span className="font-medium">{formatCurrency(taxAmount)}</span>
                  </div>
                  <div className="flex justify-between mb-3 text-lg font-semibold">
                    <span>TotalPrice:</span>
                    <span className="font-bold text-green-500">{formatCurrency(grandTotal)}</span>
                  </div>
                </div>

                <div className="mt-6 text-right">
                  <button
                    className="bg-green-500 text-white py-3 w-full rounded shadow-md hover:text-black transition-all"
                    onClick={handleVerifyPayClick}
                  >
                    Verify & Pay
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {isModalOpen && (
          <ConfirmPayment
            paymentMethod={selectedPaymentMethod}
            total={grandTotal.toFixed(2)}
            onClose={handleModalClose} // Pass the handleModalClose to remove the blur
            onProceed={handleProceed}
          />
        )}
      </div>
    </>
  );
}

export default Cash;

// import React, { useState, useEffect } from "react";
// import { useDispatch } from "react-redux";
// import { setPaymentDetails } from "../CHECK-IN/Walk-In/redux/action";
// import ConfirmPayment from "./ConfirmPayment";
// import { Link, useNavigate } from "react-router-dom";
// import { FaWallet, FaMobileAlt, FaCreditCard } from "react-icons/fa";

// function Cash() {
//   const dispatch = useDispatch();
//   const navigate = useNavigate();
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [isContentBlurred, setIsContentBlurred] = useState(false);
//   const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("Cash");

//   // Fetch booking details and guest info from local storage
//   const [bookingDetails, setBookingDetails] = useState(
//     JSON.parse(localStorage.getItem("bookingDetails")) || {}
//   );

//   // Extract payment details from local storage
//   const [pricingDetails, setPricingDetails] = useState({
//     baseAmount: 0,
//     taxAmount: 0,
//     finalAmount: 0,
//   });

//   // Extract pricing details from stored booking details
//   useEffect(() => {
//     const storedBookingDetails = JSON.parse(localStorage.getItem("bookingDetails"));
//     if (storedBookingDetails) {
//       setBookingDetails(storedBookingDetails);

//       // Extract the necessary pricing details from local storage
//       const storedPricingDetails = {
//         baseAmount: parseFloat(localStorage.getItem("baseAmount")) || 0,
//         taxAmount: parseFloat(localStorage.getItem("taxAmount")) || 0,
//         finalAmount: parseFloat(localStorage.getItem("finalAmount")) || 0,
//       };
//       setPricingDetails(storedPricingDetails);
//     }
//   }, []);

//   // Extract pricing details from the state
//   const baseAmount = pricingDetails.baseAmount || 0;
//   const taxAmount = pricingDetails.taxAmount || 0;
//   const finalAmount = pricingDetails.finalAmount || 0;

//   const guestInfo = bookingDetails.guest || {};

//   // No need for stay duration multiplication now
//   const grandTotal = finalAmount || 0;

//   // Function to format the number as Indian Rupee
//   const formatCurrency = (amount) => {
//     return `₹${amount.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,")}`; // Format with commas and ₹ symbol
//   };

//   const handleVerifyPayClick = () => {
//     setIsModalOpen(true);
//     setIsContentBlurred(true);
//   };

//   const handleProceed = () => {
//     // Dispatch payment details to the Redux store
//     dispatch(
//       setPaymentDetails({
//         paymentMethod: selectedPaymentMethod,
//         totalAmount: grandTotal.toFixed(2),
//       })
//     );

//     // Close modal and navigate to key handover page
//     setIsModalOpen(false);
//     setIsContentBlurred(false);
//     navigate("/key-handover");
//   };

//   return (
//     <div className="w-full h-screen bg-white relative">
//       <div className={`w-full h-full ${isContentBlurred ? "blur-sm" : ""}`}>
//         <div className="max-w-5xl mx-auto mt-6 p-6 bg-white rounded-md shadow-sm">
//           <div className="bg-gray-200 text-black p-3 mb-6 rounded-md">
//             <h2 className="text-xl font-semibold">Choose Your Payment Method</h2>
//           </div>

//           <div className="flex flex-col md:flex-row gap-6">
//             {/* Left Section for Payment Method */}
//             <div className="w-full md:w-1/3">
//               <div className="bg-white rounded shadow-md divide-y divide-gray-300">
//                 <Link to="/upi-payment">
//                   <button
//                     className={`py-5 px-4 w-full text-left font-medium flex items-center gap-4 transition-all ${
//                       selectedPaymentMethod === "UPI" ? "bg-gray-200" : "hover:bg-gray-200"
//                     } border-b border-gray-300`}
//                     onClick={() => setSelectedPaymentMethod("UPI")}
//                   >
//                     <FaMobileAlt />
//                     UPI
//                   </button>
//                 </Link>
//                 <Link to="/card-payment">
//                   <button
//                     className={`py-5 px-4 w-full text-left font-medium flex items-center gap-4 transition-all ${
//                       selectedPaymentMethod === "Credit/Debit/ATM Cards"
//                         ? "bg-gray-200"
//                         : "hover:bg-gray-200"
//                     } border-b border-gray-300`}
//                     onClick={() => setSelectedPaymentMethod("Credit/Debit/ATM Cards")}
//                   >
//                     <FaCreditCard />
//                     Credit/Debit/ATM Cards
//                   </button>
//                 </Link>
//                 <Link to="/cash">
//                   <button
//                     className={`py-5 px-4 w-full text-left font-medium flex items-center gap-4 transition-all ${
//                       selectedPaymentMethod === "Cash" ? "bg-gray-200" : "hover:bg-gray-200"
//                     }`}
//                     onClick={() => setSelectedPaymentMethod("Cash")}
//                   >
//                     <FaWallet />
//                     Cash
//                   </button>
//                 </Link>
//               </div>
//             </div>

//             {/* Right Section for Invoice Summary */}
//             <div className="w-full md:w-2/3 bg-white p-6 shadow-md rounded-xl">
//               <h3 className="text-xl font-semibold mb-6 border-b pb-3">Invoice Summary</h3>

//               {/* Displaying Booking and Guest Info */}
//               <div className="space-y-4 mb-6">
//                 <div className="flex justify-between text-sm">
//                   <span className="font-medium">Guest Name:</span>
//                   <span>
//                     {guestInfo.firstname} {guestInfo.lastname}
//                   </span>
//                 </div>
//                 <div className="flex justify-between text-sm">
//                   <span className="font-medium">Phone Number:</span>
//                   <span>{guestInfo.phonenumber}</span>
//                 </div>
//                 <div className="flex justify-between text-sm">
//                   <span className="font-medium">Email:</span>
//                   <span>{guestInfo.emailid}</span>
//                 </div>
//                 <div className="flex justify-between text-sm">
//                   <span className="font-medium">Booking Dates:</span>
//                   <span>{bookingDetails.booking?.checkindate} to {bookingDetails.booking?.checkoutdate}</span>
//                 </div>
//               </div>

//               {/* Invoice Breakdown */}
//               <div className="space-y-4">
//                 <div className="flex justify-between mb-3 text-sm">
//                   <span className="font-medium">BasePrice:</span>
//                   <span className="font-medium">{formatCurrency(baseAmount)}</span>
//                 </div>
//                 <div className="flex justify-between mb-3 text-sm">
//                   <span className="font-medium">GST:</span>
//                   <span className="font-medium">{formatCurrency(taxAmount)}</span>
//                 </div>
//                 <div className="flex justify-between mb-3 text-lg font-semibold">
//                   <span>TotalPrice:</span>
//                   <span className="font-bold text-green-500">{formatCurrency(grandTotal)}</span>
//                 </div>
//               </div>

//               <div className="mt-6 text-right">
//                 <button
//                   className="bg-green-500 text-white py-3 w-full rounded shadow-md hover:text-black transition-all"
//                   onClick={handleVerifyPayClick}
//                 >
//                   Verify & Pay
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>

//       {isModalOpen && (
//         <ConfirmPayment
//           paymentMethod={selectedPaymentMethod}
//           total={grandTotal.toFixed(2)}
//           onClose={() => setIsModalOpen(false)}
//           onProceed={handleProceed}
//         />
//       )}
//     </div>
//   );
// }

// export default Cash;

// import React, { useState } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import { setPaymentDetails } from "../CHECK-IN/Walk-In/redux/action";
// import ConfirmPayment from "./ConfirmPayment"; // Import ConfirmPayment component
// import { Link, useNavigate } from "react-router-dom";
// import { FaWallet, FaMobileAlt, FaCreditCard } from "react-icons/fa";

// function Cash() {
//   const dispatch = useDispatch();
//   const navigate = useNavigate();
//   const { paymentDetails } = useSelector((state) => state.paymentDetails);

//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [isContentBlurred, setIsContentBlurred] = useState(false);
//   const [selectedPaymentMethod, setSelectedPaymentMethod] =
//     useState("Cash");

//   const roomRatePerNight = 100;
//   const stayDuration = 3;
//   const taxesPerNight = 15;
//   const depositAmount = 50;

//   const serviceChargePercent = 10;
//   const CGSTPercent = 9;
//   const SGSTPercent = 9;

//   const totalRoomCost = roomRatePerNight * stayDuration;
//   const totalTaxes = taxesPerNight * stayDuration;
//   const serviceCharge = (serviceChargePercent / 100) * totalRoomCost;
//   const totalCGST = (CGSTPercent / 100) * totalRoomCost;
//   const totalSGST = (SGSTPercent / 100) * totalRoomCost;
//   const grandTotal =
//     totalRoomCost +
//     totalTaxes +
//     serviceCharge +
//     totalCGST +
//     totalSGST +
//     depositAmount;

//   const handleVerifyPayClick = () => {
//     setIsModalOpen(true);
//     setIsContentBlurred(true);
//   };

//   const handleProceed = () => {
//     // Dispatch payment details to the Redux store
//     dispatch(
//       setPaymentDetails({
//         paymentMethod: selectedPaymentMethod,
//         totalAmount: grandTotal.toFixed(2),
//       })
//     );

//     // Close modal and navigate to key handover page
//     setIsModalOpen(false);
//     setIsContentBlurred(false);
//     navigate("/key-handover");
//   };

//   return (
//     <div className="w-full h-screen bg-white relative">
//       <div className={`w-full h-full ${isContentBlurred ? "blur-sm" : ""}`}>
//         <div className="max-w-5xl mx-auto mt-6 p-6 bg-white rounded-md shadow-sm">
//           <div className="bg-gray-200 text-black p-3 mb-6 rounded-md">
//             <h2 className="text-xl font-semibold">
//               Choose Your Payment Method
//             </h2>
//           </div>

//           <div className="flex flex-col md:flex-row gap-6">
//             <div className="w-full md:w-1/3">
//               <div className="bg-white rounded shadow-md divide-y divide-gray-300">
//                 <Link to="/upi-payment">
//                   <button
//                     className={`py-5 px-4 w-full text-left font-medium flex items-center gap-4 transition-all ${
//                       selectedPaymentMethod === "UPI"
//                         ? "bg-gray-200"
//                         : "hover:bg-gray-200"
//                     } border-b border-gray-300`}
//                     onClick={() => setSelectedPaymentMethod("UPI")}
//                   >
//                     <FaMobileAlt />
//                     UPI
//                   </button>
//                 </Link>
//                 <Link to="/card-payment">
//                   <button
//                     className={`py-5 px-4 w-full text-left font-medium flex items-center gap-4 transition-all ${
//                       selectedPaymentMethod === "Credit/Debit/ATM Cards"
//                         ? "bg-gray-200"
//                         : "hover:bg-gray-200"
//                     } border-b border-gray-300`}
//                     onClick={() =>
//                       setSelectedPaymentMethod("Credit/Debit/ATM Cards")
//                     }
//                   >
//                     <FaCreditCard />
//                     Credit/Debit/ATM Cards
//                   </button>
//                 </Link>
//                 <Link to="/cash">
//                   <button
//                     className={`py-5 px-4 w-full text-left font-medium flex items-center gap-4 transition-all ${
//                       selectedPaymentMethod === "Cash"
//                         ? "bg-gray-200"
//                         : "hover:bg-gray-200"
//                     }`}
//                     onClick={() => setSelectedPaymentMethod("Cash")}
//                   >
//                     <FaWallet />
//                     Cash
//                   </button>
//                 </Link>
//               </div>
//             </div>

//             <div className="w-full md:w-2/3 bg-white p-6 shadow-md rounded-xl">
//               <h3 className="text-xl font-semibold mb-6 border-b pb-3">
//                 Invoice Summary
//               </h3>

//               <div className="space-y-4">
//                 <div className="flex justify-between mb-3 text-sm">
//                   <span className="font-medium">
//                     Room Rate ({stayDuration} nights):
//                   </span>
//                   <span className="font-medium">
//                     ${totalRoomCost.toFixed(2)}
//                   </span>
//                 </div>
//                 <div className="flex justify-between mb-3 text-sm">
//                   <span className="font-medium">
//                     Taxes ({stayDuration} nights):
//                   </span>
//                   <span className="font-medium">${totalTaxes.toFixed(2)}</span>
//                 </div>
//                 <div className="flex justify-between mb-3 text-sm">
//                   <span className="font-medium">
//                     Service Charge ({serviceChargePercent}%):
//                   </span>
//                   <span className="font-medium">
//                     ${serviceCharge.toFixed(2)}
//                   </span>
//                 </div>
//                 <div className="flex justify-between mb-3 text-sm">
//                   <span className="font-medium">CGST ({CGSTPercent}%):</span>
//                   <span className="font-medium">${totalCGST.toFixed(2)}</span>
//                 </div>
//                 <div className="flex justify-between mb-3 text-sm">
//                   <span className="font-medium">SGST ({SGSTPercent}%):</span>
//                   <span className="font-medium">${totalSGST.toFixed(2)}</span>
//                 </div>
//                 <div className="flex justify-between mb-3 text-lg font-semibold">
//                   <span>Total:</span>
//                   <span className="font-bold text-green-500">
//                     ${grandTotal.toFixed(2)}
//                   </span>
//                 </div>
//               </div>

//               <div className="mt-6 text-right">
//                 <button
//                   className="bg-green-500 text-white py-3 w-full rounded shadow-md hover:text-black transition-all"
//                   onClick={handleVerifyPayClick}
//                 >
//                   Verify & Pay
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>

//       {isModalOpen && (
//         <ConfirmPayment
//           paymentMethod={selectedPaymentMethod}
//           total={grandTotal.toFixed(2)}
//           onClose={() => setIsModalOpen(false)}
//           onProceed={handleProceed}
//         />
//       )}
//     </div>
//   );
// }

// export default Cash;
